// Key of the custom USER field "contrat_accepte", configured in Sharetribe Console
// (Build > Content > User fields, public scope). Saved once per account, on the user's
// profile publicData, rather than per listing — so it doesn't need to be re-accepted for
// every additional listing (e.g. one per service area) created under the same account.
export const CONTRACT_FIELD_KEY = 'contrat_accepte';

// Sharetribe validates natively that at least one of these is checked. The platform also
// requires both to be checked before the provider can continue, since each covers a distinct
// commitment (accepting the subscription terms, confirming certification/association status).
// This is enforced in code (allOptionsSelected validator), not natively, since the Console
// field is intentionally not required at the Sharetribe level.
export const CONTRACT_REQUIRED_OPTION_KEYS = ['accepte', 'certification_confirmee'];

// The contract's checkbox options are fixed legal text, not editable per-marketplace-config,
// so they're hardcoded here rather than read from the Console field's enumOptions. This also
// sidesteps a Console-config pitfall: a field with a mismatched listingType/userType
// restriction gets silently dropped from config.user.userFields (see validUserFields in
// configHelpers.js), which would otherwise make the checkboxes vanish without explanation.
export const CONTRACT_OPTIONS = [
  {
    key: 'accepte',
    label: "J'ai lu et j'accepte les conditions de l'entente d'abonnement",
  },
  {
    key: 'certification_confirmee',
    label:
      'Je confirme détenir une certification valide et une association professionnelle active',
  },
];

// Only listings of this type require contract acceptance before the provider can continue.
// Note: this is the actual listingType slug stored in publicData.listingType (confirmed via
// Console/network inspection), not the human-readable "Massothérapeute" label shown in Console.
export const MASSOTHERAPEUTE_LISTING_TYPE = 'soin-massotherapie';

/**
 * Whether the given user has already accepted the subscription agreement (i.e. both required
 * options are present in their profile's publicData.contrat_accepte).
 *
 * @param {Object} currentUser the current user entity (e.g. from ensureCurrentUser)
 * @returns {boolean}
 */
export const hasAcceptedContract = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const accepted = publicData[CONTRACT_FIELD_KEY];
  return (
    Array.isArray(accepted) && CONTRACT_REQUIRED_OPTION_KEYS.every(key => accepted.includes(key))
  );
};

const NAME_PLACEHOLDER = '[Nom du/de la massothérapeute]';

/**
 * Builds the subscription agreement text, with the provider's display name inserted in the
 * preamble and appended as a signature line at the end.
 *
 * @param {string} massotherapeuteName display name of the massotherapeute accepting the contract
 * @returns {string} full contract text
 */
export const getContractText = massotherapeuteName => {
  const name = massotherapeuteName || NAME_PLACEHOLDER;

  return `Entente d'abonnement — Phase de prélancement
MassageDerniereMinute.com

Entre Guylaine Robidoux, fondatrice de MassageDerniereMinute.com (« la Plateforme »)
et ${name} (« le/la Massothérapeute »)

1. Objet
Cette entente confirme les modalités d'abonnement du/de la Massothérapeute à la Plateforme durant la phase de prélancement.

2. Durée et tarif

Le tarif de votre abonnement est de 25 $/mois, sans engagement — résiliable en tout temps sur simple avis.

Offre de prélancement : 14,99 $/mois jusqu'au 15 août 2026, puis 25 $/mois par la suite.

3. Mode de paiement
Le paiement s'effectue par virement Interac à info@massagederniereminute.com, avant le [date] de chaque mois.

4. Ce que l'abonnement inclut

Publication d'une annonce professionnelle sur la Plateforme
Visibilité auprès des clients recherchant un massage de dernière minute
Aucune commission prélevée sur les séances

5. Nature de la relation
Le/la Massothérapeute demeure un travailleur ou une travailleuse autonome, responsable de sa pratique, de ses certifications et du respect des exigences de son association professionnelle. La Plateforme agit uniquement comme intermédiaire de mise en relation et n'est pas un employeur.

6. Vérification
Le/la Massothérapeute confirme détenir une certification valide et une association professionnelle active (RMPQ, FQM, RITMA, RMQ, AMQ, ou AQTN), et s'engage à fournir une preuve de cette certification sur demande.

7. Résiliation
Chaque partie peut mettre fin à cette entente en tout temps, avec un préavis raisonnable par courriel.

Les paiements effectués ne sont pas remboursables, sauf disposition contraire prévue par la loi applicable au Québec.

8. Loi applicable
Cette entente est régie par les lois de la province de Québec, Canada.

Signé électroniquement par acceptation des présentes conditions.

Nom du/de la massothérapeute : ${name}`;
};
